import * as THREE from '@phdsoft/three';
import { Modes } from './mode';
import { isLightModelEdges } from 'src/app/digital-twin/Loader/light-model-edges';

/** Inputs of {@link repairBordersVisible}. */
export interface RepairBorderVisibilityInput {
    /** Asset.isNewModelMode (new-format GLTF regions). */
    isNewModelMode: boolean;
    /** Asset.mode; undefined before the first mode switch. */
    mode: Modes | null | undefined;
    /** SectionBorderSettings.show. */
    show: boolean;
}

/**
 * Repair elements (RepairCampaigns) draw their borders as LineLoop/LineSegments children of their
 * SurfaceDesign (GeometricElement.createBorder), outside section.borderGroup, so the persisted
 * "show section borders" setting has to reach them separately.
 *
 * Same coverage as the structure Borders (Asset.sectionBordersSettingApplies): new-format assets
 * in MODEL and REGIONS follow the setting; SECTIONS and old-format assets always show the borders.
 *
 * MODEL on a light-model asset also follows the setting: the structure Borders are kept hidden there
 * (the light model replaces the structure, so sectionBordersSettingApplies is false), and the
 * light-model crease edges, the only line work shown, follow the setting. Repairs are normally not
 * shown in that view; if they are, their borders match the light-model edges instead of being forced on.
 */
export function repairBordersVisible({ isNewModelMode, mode, show }: RepairBorderVisibilityInput): boolean {
    if (!isNewModelMode) return true;
    if (mode === 'MODEL' || mode === 'REGIONS') return show !== false;
    return true;
}

/**
 * Sets `.visible` on the border lines (Line/LineLoop/LineSegments children) of one element's
 * SurfaceDesign. Never touches the SurfaceDesign itself (the timeline filter owns its visibility),
 * non-line children, or light-model crease edges.
 * @returns the number of lines whose visibility changed.
 */
export function setRepairBorderLinesVisible(surfaceDesign: THREE.Object3D | null | undefined, visible: boolean): number {
    const children = surfaceDesign?.children;
    if (!children || children.length === 0) return 0;
    let changed = 0;
    for (const child of children) {
        if (!(child as THREE.Line).isLine || isLightModelEdges(child)) continue;
        if (child.visible !== visible) {
            child.visible = visible;
            changed++;
        }
    }
    return changed;
}

/**
 * Element overlay borders: the rule above is not repair-specific. Every element overlay that draws its
 * own outline with GeometricElement.createBorder (line children of its SurfaceDesign, outside
 * section.borderGroup) follows it: repairs (RepairCampaigns) and critical areas (CriticalAreaCampaigns,
 * see Asset.applyCriticalAreaBorderVisibility). The repair names stay as the original API.
 */
export const elementOverlayBordersVisible = repairBordersVisible;

/** {@link setRepairBorderLinesVisible} under its element-overlay name (repairs, critical areas). */
export const setElementOverlayBorderLinesVisible = setRepairBorderLinesVisible;
